import { ReactNode } from "react"

import { Table as TanstackTable, flexRender } from "@tanstack/react-table"

import { Spinner } from "@/components/ui/spinner"

interface EntityTableProps<TData> {
  table: TanstackTable<TData>
  getRowProps?: (row: TData) => {
    key: string
    isSelected?: boolean
    onClick?: (e: React.MouseEvent) => void
    dataAttributes?: Record<string, string>
  }
  footer: ReactNode
  loadingMore?: boolean
  hasMore?: boolean
  endOfListMessage?: string
  scrollContainerRef?: React.RefObject<HTMLDivElement>
  sentinelRef?: React.RefObject<HTMLDivElement>
}

export function EntityTable<TData>({
  table,
  getRowProps,
  footer,
  loadingMore = false,
  hasMore,
  endOfListMessage = "End of list",
  scrollContainerRef,
  sentinelRef,
}: EntityTableProps<TData>) {
  const columnCount = table.getAllColumns().length
  const rows = table.getRowModel().rows

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <table className="w-full min-w-4xl border-separate border-spacing-0">
          <thead className="sticky top-0 z-10 border border-palette-jungleGreen200 bg-palette-jungleGreen000">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="whitespace-nowrap border-y border-palette-jungleGreen200 px-4 py-3 text-left text-xs font-normal text-palette-jungleGreen900 first:rounded-tl-lg first:border-l last:rounded-tr-lg last:border-r"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="py-8 text-center text-sm text-palette-jungleGreen500"
                >
                  No records match these filters
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const rowProps = getRowProps?.(row.original)
                return (
                  <tr
                    key={rowProps?.key ?? row.id}
                    {...rowProps?.dataAttributes}
                    onClick={rowProps?.onClick}
                    className={`border-b border-palette-jungleGreen000 transition-colors hover:bg-palette-jungleGreen000 ${
                      rowProps?.onClick ? "cursor-pointer" : ""
                    } ${rowProps?.isSelected ? "bg-palette-blueCrayola000" : ""}`}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => (
                      <td
                        key={cell.id}
                        className={`whitespace-nowrap px-4 py-3 text-sm text-palette-jungleGreen700 ${
                          cellIndex === 0 && rowProps?.isSelected
                            ? "border-l-[0.375rem] border-l-palette-blueCrayola500"
                            : ""
                        }`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
        {sentinelRef && <div ref={sentinelRef} className="h-1" />}
        {loadingMore && (
          <div className="flex items-center justify-center py-4">
            <Spinner className="mr-2" />
            <span className="text-sm text-palette-jungleGreen500">Loading more...</span>
          </div>
        )}
        {hasMore === false && rows.length > 0 && (
          <p className="py-4 text-center text-sm text-palette-jungleGreen400">{endOfListMessage}</p>
        )}
      </div>
      <div className="border-t border-palette-jungleGreen100 bg-palette-jungleGreen100 px-4 py-2">
        <p className="text-center text-sm text-palette-jungleGreen500">{footer}</p>
      </div>
    </div>
  )
}
